import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Course, Section, Lesson, EnrollmentProgress, UserBadge } from '@/lib/types';
import CourseLanding from '@/components/courses/CourseLanding';
import CourseSidebar from '@/components/learning/CourseSidebar';
import CourseAssignments from '@/components/learning/CourseAssignments';
import LessonViewer from '@/components/learning/LessonViewer';
import CertificateRequirements from '@/components/certificates/CertificateRequirements';
import { useAuth } from '@/contexts/AuthContext';

function pickResumeLesson(ordered: Lesson[], completedIds: number[]): Lesson | null {
    if (ordered.length === 0) return null;
    const incomplete = ordered.find((l) => !completedIds.includes(l.id));
    return incomplete ?? ordered[ordered.length - 1];
}

function orderLessonsBySection(sortedSections: Section[], allLessons: Lesson[]): Lesson[] {
    const ordered: Lesson[] = [];
    sortedSections.forEach((section) => {
        const sectionLessons = allLessons
            .filter((l) => l.section === section.id)
            .sort((a, b) => a.order - b.order);
        ordered.push(...sectionLessons);
    });
    return ordered;
}

export default function CourseLearningPage() {
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const courseId = id ? parseInt(id, 10) : undefined;

    const [course, setCourse] = useState<Course | null>(null);
    const [sections, setSections] = useState<Section[]>([]);
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [isEnrolled, setIsEnrolled] = useState(false);
    const [enrollmentId, setEnrollmentId] = useState<number | null>(null);
    const [isTutorOwner, setIsTutorOwner] = useState(false);
    const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
    const [completedLessonIds, setCompletedLessonIds] = useState<number[]>([]);
    const [lessonToTracker, setLessonToTracker] = useState<Record<number, number>>({});
    const [overallProgress, setOverallProgress] = useState(0);
    const [completing, setCompleting] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [awardedBadges, setAwardedBadges] = useState<UserBadge[]>([]);

    const applyProgress = useCallback((progress: EnrollmentProgress): number[] => {
        const completed: number[] = [];
        const map: Record<number, number> = {};
        progress.sections.forEach((section) => {
            section.lessons.forEach((lesson) => {
                map[lesson.id] = lesson.tracker_id;
                if (lesson.is_completed) {
                    completed.push(lesson.id);
                }
            });
        });
        setLessonToTracker(map);
        setCompletedLessonIds(completed);
        setOverallProgress(progress.overall_progress);
        return completed;
    }, []);

    const loadProgress = useCallback(async (enrollId: number): Promise<number[]> => {
        const response = await apiClient.getEnrollmentProgress(enrollId);
        if (response.success && response.data) {
            return applyProgress(response.data as EnrollmentProgress);
        }
        return [];
    }, [applyProgress]);

    useEffect(() => {
        if (courseId) {
            loadCourseData();
        }
    }, [courseId, user?.id]);

    const loadCourseData = async () => {
        if (!courseId) return;

        try {
            setLoading(true);
            setError(null);

            const courseResponse = await apiClient.getCourse(courseId);
            if (!courseResponse.success || !courseResponse.data) {
                throw new Error('Failed to load course');
            }

            const courseData = courseResponse.data as Course;
            setCourse(courseData);

            // Prefer nested sections/lessons from course detail
            let orderedLessons: Lesson[] = [];
            const nestedSections = (courseData.sections || []).slice().sort((a, b) => a.order - b.order);
            if (nestedSections.length > 0) {
                setSections(nestedSections);
                nestedSections.forEach((section) => {
                    const sectionLessons = (section.lessons || [])
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((lesson) => ({ ...lesson, section: section.id }));
                    orderedLessons.push(...sectionLessons);
                });
            } else {
                const [sectionsRes, lessonsRes] = await Promise.all([
                    apiClient.getSections(courseId),
                    apiClient.getLessons(),
                ]);

                const sectionsData = sectionsRes.data as any;
                const sectionResults: Section[] = Array.isArray(sectionsData)
                    ? sectionsData
                    : sectionsData?.results || [];
                const sortedSections = sectionResults.sort((a, b) => a.order - b.order);
                setSections(sortedSections);

                const lessonsData = lessonsRes.data as any;
                const lessonResults: Lesson[] = Array.isArray(lessonsData)
                    ? lessonsData
                    : lessonsData?.results || [];
                const courseSectionIds = sortedSections.map((s) => s.id);
                const filteredLessons = lessonResults.filter((l) => courseSectionIds.includes(l.section));
                orderedLessons = orderLessonsBySection(sortedSections, filteredLessons);
            }
            setLessons(orderedLessons);

            let completedIds: number[] = [];
            if (user) {
                const tutorId = courseData.tutor?.id;
                if (user.role === 'tutor' && tutorId === user.id) {
                    setIsTutorOwner(true);
                    setIsEnrolled(true);
                } else {
                    const enrollmentResponse = await apiClient.getEnrollments();
                    if (enrollmentResponse.success && enrollmentResponse.data?.results) {
                        const enrollment = enrollmentResponse.data.results.find(
                            (e) => e.course?.id === courseId
                        );
                        if (enrollment) {
                            setIsEnrolled(true);
                            setEnrollmentId(enrollment.id);
                            completedIds = await loadProgress(enrollment.id);
                        }
                    }
                }
            }
            setCurrentLesson(pickResumeLesson(orderedLessons, completedIds));
        } catch (err) {
            console.error(err);
            setError('Failed to load course content');
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async () => {
        if (!courseId || enrolling) return;
        try {
            setEnrolling(true);
            const response = await apiClient.enrollInCourse(courseId);
            if (response.success && response.data) {
                setIsEnrolled(true);
                const newId = response.data.id as number;
                setEnrollmentId(newId);
                const completedIds = await loadProgress(newId);
                setCurrentLesson(pickResumeLesson(lessons, completedIds));
            } else {
                alert('Enrollment failed: ' + response.error);
            }
        } catch {
            alert('Enrollment failed');
        } finally {
            setEnrolling(false);
        }
    };

    const handleLessonComplete = async () => {
        if (!currentLesson || completing) return;

        if (isTutorOwner) {
            if (!completedLessonIds.includes(currentLesson.id)) {
                setCompletedLessonIds([...completedLessonIds, currentLesson.id]);
            }
            return;
        }

        const trackerId = lessonToTracker[currentLesson.id];
        if (!trackerId) {
            alert('Progress tracker not found for this lesson. Try reloading the page.');
            return;
        }

        if (completedLessonIds.includes(currentLesson.id)) {
            return;
        }

        try {
            setCompleting(true);
            const response = await apiClient.completeLesson(trackerId);
            if (response.success) {
                setCompletedLessonIds((prev) =>
                    prev.includes(currentLesson.id) ? prev : [...prev, currentLesson.id]
                );
                if (typeof response.data?.progress === 'number') {
                    setOverallProgress(response.data.progress);
                }
                const newlyAwarded = Array.isArray(response.data?.awarded_badges)
                    ? (response.data.awarded_badges as UserBadge[])
                    : [];
                if (newlyAwarded.length > 0) {
                    setAwardedBadges(newlyAwarded);
                }
                if (enrollmentId) {
                    await loadProgress(enrollmentId);
                }
            } else {
                alert(response.error || 'Failed to mark lesson complete');
            }
        } finally {
            setCompleting(false);
        }
    };

    const handleTimeUpdate = async (seconds: number) => {
        if (!currentLesson || isTutorOwner) return;
        const trackerId = lessonToTracker[currentLesson.id];
        if (!trackerId) return;
        await apiClient.updateLessonTime(trackerId, seconds);
    };

    const handleSelectLesson = (lesson: Lesson) => {
        setAwardedBadges([]);
        setCurrentLesson(lesson);
    };

    const adjacentLessons = (() => {
        if (!currentLesson || lessons.length === 0) return { prev: undefined, next: undefined };

        const orderedLessons: Lesson[] = [];
        sections.forEach((section) => {
            const sectionLessons = lessons
                .filter((l) => l.section === section.id)
                .sort((a, b) => a.order - b.order);
            orderedLessons.push(...sectionLessons);
        });

        const currentIndex = orderedLessons.findIndex((l) => l.id === currentLesson.id);
        return {
            prev: currentIndex > 0 ? orderedLessons[currentIndex - 1] : undefined,
            next: currentIndex < orderedLessons.length - 1 ? orderedLessons[currentIndex + 1] : undefined,
        };
    })();

    if (loading) {
        return (
            <div className="flex justify-center items-center py-20 min-h-full">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !course) {
        return (
            <div className="p-8 text-center text-red-600">
                {error || 'Course not found'}
                <br />
                <Link to="/courses" className="text-blue-600 underline mt-4 block">Back to Courses</Link>
            </div>
        );
    }

    if (!isEnrolled) {
        const catalogLessons = user?.role === 'student'
            ? lessons.filter(l => l.status === 'published')
            : lessons;
        return (
            <CourseLanding
                course={course}
                sections={sections}
                lessons={catalogLessons}
                onEnroll={handleEnroll}
                enrolling={enrolling}
            />
        );
    }

    return (
        <div className="flex flex-col h-full bg-gray-100 overflow-hidden">
            <div className="bg-white shadow-sm z-10 flex items-center justify-between px-6 py-3">
                <div className="flex items-center space-x-4">
                    <Link to="/courses" className="text-gray-500 hover:text-gray-900">
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-lg font-bold text-gray-800 truncate">{course.title}</h1>
                    {!isTutorOwner && (
                        <span className="text-sm text-gray-500">{overallProgress.toFixed(0)}% complete</span>
                    )}
                    {!isTutorOwner && (
                        <Link to="/certificates" className="text-sm text-blue-600 hover:underline">
                            Certificates
                        </Link>
                    )}
                </div>
                <div>
                    <Link to="/dashboard" className="text-sm text-blue-600 hover:underline">Dashboard</Link>
                </div>
            </div>

            <div className="flex flex-1 overflow-hidden">
                <CourseSidebar
                    sections={sections}
                    lessons={lessons}
                    title="Course Content"
                    currentLessonId={currentLesson?.id}
                    completedLessonIds={completedLessonIds}
                    onSelectLesson={handleSelectLesson}
                />

                <div className="flex-1 relative flex flex-col min-h-0">
                    {awardedBadges.length > 0 && (
                        <div className="mx-4 mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                            <p className="font-medium">
                                Badge{awardedBadges.length > 1 ? 's' : ''} unlocked:{' '}
                                {awardedBadges.map((b) => b.badge.name).join(', ')}
                            </p>
                            <Link to="/badges" className="mt-1 inline-block text-emerald-700 underline">
                                View your badges
                            </Link>
                        </div>
                    )}
                    {!isTutorOwner && (
                        <div className="mx-4 mt-3">
                            <CertificateRequirements course={course} lessonProgress={overallProgress} />
                        </div>
                    )}
                    {!isTutorOwner && courseId && (
                        <CourseAssignments courseId={courseId} />
                    )}
                    {currentLesson ? (
                        <div className="flex-1 min-h-0">
                        <LessonViewer
                            lesson={currentLesson}
                            isCompleted={completedLessonIds.includes(currentLesson.id)}
                            onComplete={handleLessonComplete}
                            onTimeUpdate={isTutorOwner ? undefined : handleTimeUpdate}
                            onNext={adjacentLessons.next ? () => handleSelectLesson(adjacentLessons.next!) : undefined}
                            onPrevious={adjacentLessons.prev ? () => handleSelectLesson(adjacentLessons.prev!) : undefined}
                        />
                        </div>
                    ) : (
                        <div className="flex flex-1 items-center justify-center h-full text-gray-500 bg-gray-50">
                            Select a lesson to start learning
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
